import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  LogOut,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Download,
  ExternalLink,
  X,
  Check,
  FileText,
  ImageIcon,
  Clock,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  Star,
  Flame,
  DollarSign,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useAdminAuth } from "@/context/AdminAuthContext";
import MessageBoard from "@/components/MessageBoard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FILTERS = [
  { id: "pending", label: "Pending review" },
  { id: "verified", label: "Verified" },
  { id: "rejected", label: "Rejected" },
  { id: "unverified", label: "Unverified" },
  { id: "docs", label: "Docs uploaded" },
  { id: "all", label: "All pros" },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function StatusBadge({ status }) {
  const map = {
    pending: { c: "bg-[#0E93EC] text-white", i: ShieldAlert, l: "Pending" },
    verified: { c: "bg-[#0E93EC] text-[#EEEEEF]", i: ShieldCheck, l: "Verified" },
    rejected: { c: "bg-[#010204] text-white", i: ShieldX, l: "Rejected" },
    unverified: { c: "bg-[#EEEEEF]/15 text-[#EEEEEF]", i: Lock, l: "Unverified" },
  };
  const cfg = map[status] || map.unverified;
  const Icon = cfg.i;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-semibold ${cfg.c}`}
      data-testid={`status-badge-${status}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.l}
    </span>
  );
}

/* ============================================================
 *  Top bar
 * ============================================================ */
function AdminTopBar({ onLogout, summary }) {
  return (
    <header className="bg-[#010204] text-[#EEEEEF] border-b border-[#C9C6C6]/10 sticky top-0 z-30">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-3">
          <img src="/brand/TPWH_Logo_Transparent.png" alt="The Pros Work Hub" className="h-9 w-auto shrink-0" data-testid="brand-logo-img" />
          <span className="font-mono-alt uppercase tracking-[0.25em] text-xs text-[#0E93EC] font-semibold">
            Admin
          </span>
        </Link>
        <div className="flex items-center gap-8">
          {summary && (
            <div className="hidden lg:flex items-center gap-6 text-xs uppercase tracking-[0.18em] font-mono-alt text-[#C9C6C6]">
              <span>
                <b className="text-[#0E93EC]">{summary.pending}</b> pending
              </span>
              <span>
                <b className="text-[#0E93EC] text-[#EEEEEF]">{summary.verified}</b> verified
              </span>
              <span>
                <b className="text-[#C9C6C6]">{summary.total}</b> total
              </span>
            </div>
          )}
          <Button
            onClick={onLogout}
            variant="ghost"
            className="rounded-none text-[#C9C6C6] hover:text-white hover:bg-[#EEEEEF]/5 gap-2"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            Log out
          </Button>
        </div>
      </div>
    </header>
  );
}

/* ============================================================
 *  Doc preview — inline image or download PDF
 * ============================================================ */
function DocPreview({ contractorId, doc, onPrevPreview }) {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setContent(null);
    setErr(null);
    axios
      .get(`${API}/admin/contractors/${contractorId}/docs/${doc.id}`, {
        withCredentials: true,
      })
      .then(({ data }) => {
        if (!cancelled) setContent(data);
      })
      .catch((e) => !cancelled && setErr(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [contractorId, doc.id]);

  const dataUrl = content
    ? `data:${content.mime_type};base64,${content.content_base64}`
    : null;
  const isImage = doc.mime_type?.startsWith("image/");
  const isPdf = doc.mime_type === "application/pdf";
  const sizeKb = Math.round(doc.size_bytes / 1024);

  return (
    <div
      className="border border-[#C9C6C6]/15 bg-[#0D1421] flex flex-col"
      data-testid={`doc-preview-${doc.id}`}
    >
      <div className="px-4 py-3 border-b border-[#C9C6C6]/10 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">
            {isImage ? (
              <ImageIcon className="w-3 h-3" />
            ) : (
              <FileText className="w-3 h-3" />
            )}
            {doc.doc_type}
          </div>
          <div className="font-mono-alt text-sm text-[#EEEEEF] truncate">
            {doc.filename}
          </div>
          <div className="text-[11px] text-[#C9C6C6]">
            {sizeKb} KB · {timeAgo(doc.uploaded_at)}
          </div>
        </div>
        {dataUrl && (
          <div className="flex items-center gap-1">
            <a
              href={dataUrl}
              download={doc.filename}
              className="p-2 hover:bg-[#EEEEEF]/8 text-[#C9C6C6] hover:text-[#EEEEEF]"
              title="Download"
              data-testid={`doc-download-${doc.id}`}
            >
              <Download className="w-4 h-4" />
            </a>
            <a
              href={dataUrl}
              target="_blank"
              rel="noreferrer"
              className="p-2 hover:bg-[#EEEEEF]/8 text-[#C9C6C6] hover:text-[#EEEEEF]"
              title="Open in new tab"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
      <div className="flex-1 bg-[#010204] min-h-[220px] flex items-center justify-center overflow-hidden">
        {loading && (
          <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC]" />
        )}
        {err && (
          <div className="text-sm text-[#EEEEEF] p-4">Failed to load doc.</div>
        )}
        {content && isImage && (
          <img
            src={dataUrl}
            alt={doc.filename}
            className="max-w-full max-h-[420px] object-contain"
          />
        )}
        {content && isPdf && (
          <iframe
            src={dataUrl}
            title={doc.filename}
            className="w-full h-[420px] bg-[#010204]"
          />
        )}
        {content && !isImage && !isPdf && (
          <div className="p-6 text-center text-[#C9C6C6]">
            <FileText className="w-8 h-8 mx-auto mb-2" />
            <div className="text-sm">Preview not available</div>
            <a
              href={dataUrl}
              download={doc.filename}
              className="mt-3 inline-block text-[#0E93EC] font-medium underline"
            >
              Download to view
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 *  Contractor detail drawer
 * ============================================================ */
function ContractorDrawer({ contractorId, open, onOpenChange, onDone }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/contractors/${contractorId}`,
        { withCredentials: true }
      );
      setDetail(data);
    } catch {
      toast.error("Could not load pro");
    } finally {
      setLoading(false);
    }
  }, [contractorId]);

  useEffect(() => {
    if (open) {
      setRejectMode(false);
      setRejectReason("");
      load();
    }
  }, [open, load]);

  const verify = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/contractors/${contractorId}/verify`,
        {},
        { withCredentials: true }
      );
      toast.success("Pro verified");
      onDone?.();
      onOpenChange(false);
    } catch {
      toast.error("Could not verify");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please enter a rejection reason");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/contractors/${contractorId}/reject`,
        { reason: rejectReason.trim() },
        { withCredentials: true }
      );
      toast.success("Pro rejected");
      onDone?.();
      onOpenChange(false);
    } catch {
      toast.error("Could not reject");
    } finally {
      setBusy(false);
    }
  };

  const c = detail;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-[#0D1421] p-0 overflow-y-auto"
        data-testid="admin-contractor-drawer"
      >
        <SheetHeader className="p-6 border-b border-[#C9C6C6]/10 text-left space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">
              Pro review
            </div>
            {c && <StatusBadge status={c.verification_status} />}
          </div>
          <SheetTitle className="font-editorial text-3xl md:text-4xl font-light text-[#EEEEEF] tracking-tighter">
            {c?.business_name || c?.name || "Pro"}
          </SheetTitle>
          <SheetDescription className="text-[#C9C6C6] text-xs font-mono-alt">
            ID {c?.id?.slice(0, 8)}…{c?.id?.slice(-6)}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="p-10 flex items-center gap-3 text-[#C9C6C6]">
            <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC]" />
            Loading pro…
          </div>
        )}

        {!loading && c && (
          <div className="p-6 space-y-8">
            {/* Contact facts */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: Mail, label: "Email", value: c.email },
                { icon: Phone, label: "Phone", value: c.phone || "—" },
                { icon: Briefcase, label: "Trade", value: c.trade || "—" },
                {
                  icon: MapPin,
                  label: "Service area",
                  value: c.service_area || "—",
                },
                { icon: Clock, label: "Signed up", value: timeAgo(c.created_at) },
                {
                  icon: Clock,
                  label: "Submitted",
                  value: timeAgo(c.submitted_for_verification_at),
                },
              ].map((r) => {
                const Icon = r.icon;
                return (
                  <div
                    key={r.label}
                    className="border border-[#C9C6C6]/10 p-3"
                  >
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#C9C6C6] mb-1">
                      <Icon className="w-3 h-3" /> {r.label}
                    </div>
                    <div className="text-sm text-[#EEEEEF] truncate font-medium">
                      {r.value}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Work preferences */}
            {((c.work_zones?.length || 0) > 0 ||
              (c.job_preferences?.length || 0) > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(c.work_zones?.length || 0) > 0 && (
                  <div className="border border-[#C9C6C6]/10 p-4">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6] mb-3">
                      Where they'll work
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="admin-workzones">
                      {c.work_zones.map((z) => (
                        <span
                          key={z}
                          className="text-[11px] uppercase tracking-[0.15em] font-semibold bg-[#0E93EC] text-[#EEEEEF] px-2 py-1"
                        >
                          {z.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(c.job_preferences?.length || 0) > 0 && (
                  <div className="border border-[#C9C6C6]/10 p-4">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6] mb-3">
                      Job value preferences
                    </div>
                    <ul className="space-y-1.5 text-[12px] text-[#EEEEEF]" data-testid="admin-jobprefs">
                      {c.job_preferences.map((p) => {
                        const map = {
                          min_250: "No jobs under $250",
                          drive_100mi_2500: "Drive 100 mi for $2,500+",
                          statewide_commercial_10k:
                            "Statewide for commercial $10,000+",
                        };
                        return (
                          <li key={p} className="flex items-start gap-1.5">
                            <span className="mt-1 w-1.5 h-1.5 bg-[#0E93EC] shrink-0" />
                            {map[p] || p}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {c.rejection_reason && c.verification_status === "rejected" && (
              <div className="border-l-2 border-[#C9C6C6] bg-[#010204]/5 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#EEEEEF] mb-1">
                  Previous rejection reason
                </div>
                <p className="text-sm text-[#EEEEEF]">{c.rejection_reason}</p>
              </div>
            )}

            {/* External profile links */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6] mb-3">
                External profile links · {c.external_profile_links?.length || 0}
              </div>
              {(c.external_profile_links?.length || 0) === 0 ? (
                <div className="text-sm text-[#C9C6C6] italic border border-dashed border-[#C9C6C6]/20 p-4">
                  No links on file. (Signups after this feature require at least one.)
                </div>
              ) : (
                <ul className="space-y-2" data-testid="admin-contractor-links">
                  {c.external_profile_links.map((url, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-3 border border-[#C9C6C6]/10 px-4 py-3"
                    >
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-sm text-[#0E93EC] hover:text-[#010204] underline underline-offset-4 truncate flex-1"
                        data-testid={`admin-contractor-link-${i}`}
                      >
                        {url}
                      </a>
                      <ExternalLink className="w-4 h-4 text-[#C9C6C6] shrink-0" />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Docs */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6] mb-3">
                Uploaded documents · {c.verification_docs?.length || 0}
              </div>
              {(c.verification_docs?.length || 0) === 0 ? (
                <div className="text-sm text-[#C9C6C6] italic border border-dashed border-[#C9C6C6]/20 p-6 text-center">
                  No documents uploaded yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {c.verification_docs.map((d) => (
                    <DocPreview
                      key={d.id}
                      contractorId={c.id}
                      doc={d}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            {c.verification_status !== "verified" && (
              <div className="sticky bottom-0 bg-[#0D1421] -mx-6 px-6 py-4 border-t border-[#C9C6C6]/10">
                {!rejectMode ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={verify}
                      disabled={busy}
                      className="group flex-1 bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-6 pr-5 font-semibold uppercase tracking-[0.18em] text-sm"
                      data-testid="verify-button"
                    >
                      <ShieldCheck className="w-4 h-4 mr-3" />
                      <span className="flex-1 text-left">Verify pro</span>
                      <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC]">
                        <Check className="w-4 h-4" />
                      </span>
                    </Button>
                    <Button
                      onClick={() => setRejectMode(true)}
                      disabled={busy}
                      variant="outline"
                      className="border-[#C9C6C6] text-[#EEEEEF] hover:bg-[#010204]/5 rounded-none h-14 px-5 font-semibold uppercase tracking-[0.18em] text-sm"
                      data-testid="reject-open-button"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]">
                      Rejection reason (visible to pro)
                    </Label>
                    <Textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={3}
                      placeholder="e.g. License doc is expired. Please re-upload a current one."
                      className="mt-2 bg-[#010204] border-0 rounded-none focus-visible:ring-2 focus-visible:ring-[#C9C6C6]"
                      data-testid="reject-reason-input"
                    />
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={reject}
                        disabled={busy || !rejectReason.trim()}
                        className="bg-[#010204] hover:bg-[#C9C6C6] text-white rounded-none h-11 px-6"
                        data-testid="reject-confirm-button"
                      >
                        Confirm rejection
                      </Button>
                      <Button
                        onClick={() => setRejectMode(false)}
                        disabled={busy}
                        variant="ghost"
                        className="rounded-none h-11 px-4 text-[#C9C6C6] hover:text-[#EEEEEF]"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ============================================================
 *  Active Jobs view
 * ============================================================ */
function ActiveJobsView() {
  const [filter, setFilter] = useState("active");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listResp, sumResp] = await Promise.all([
        axios.get(`${API}/admin/jobs?filter=${filter}`, { withCredentials: true }),
        axios.get(`${API}/admin/jobs/summary`, { withCredentials: true }),
      ]);
      setRows(listResp.data);
      setSummary(sumResp.data);
    } catch {
      toast.error("Could not load jobs");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const filters = [
    { id: "active", label: "Active" },
    { id: "flagged", label: `Flagged >7d${summary?.flagged ? " · " + summary.flagged : ""}` },
    { id: "complete", label: "Completed" },
    { id: "all", label: "All" },
  ];

  const statusColors = {
    estimating: "bg-[#0E93EC] text-white",
    accepted: "bg-[#0E93EC] text-[#EEEEEF]",
    in_progress: "bg-[#0E93EC] text-[#EEEEEF]",
    completed: "bg-[#EEEEEF]/15 text-white",
    canceled: "bg-[#C9C6C6]/30 text-white",
  };

  return (
    <div data-testid="active-jobs-view">
      {/* Summary tiles */}
      <div className="bg-[#0D1421] border-b border-[#C9C6C6]/8">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-5 gap-6">
          {[
            { label: "Estimating", value: summary?.estimating ?? 0, testid: "js-estimating" },
            { label: "Accepted", value: summary?.accepted ?? 0, testid: "js-accepted" },
            { label: "In progress", value: summary?.in_progress ?? 0, testid: "js-inprogress" },
            { label: "Completed", value: summary?.completed ?? 0, testid: "js-completed" },
            { label: "Flagged >7d", value: summary?.flagged ?? 0, testid: "js-flagged" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid}>
              <div className="font-editorial text-4xl font-light text-[#EEEEEF] tracking-tighter leading-none">
                {s.value}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-[#0D1421] border-b border-[#C9C6C6]/8">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-3 flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-[11px] uppercase tracking-[0.22em] font-semibold px-3 py-2 border transition-colors ${
                filter === f.id
                  ? "bg-[#010204] text-white border-[#C9C6C6]"
                  : "bg-transparent text-[#EEEEEF] border-[#C9C6C6]/20 hover:border-[#C9C6C6]"
              }`}
              data-testid={`jobs-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        {loading ? (
          <div className="p-10 flex items-center gap-3 text-[#C9C6C6]">
            <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC]" /> Loading jobs…
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-[#C9C6C6]/20 p-12 text-center">
            <Briefcase className="w-8 h-8 mx-auto mb-3 text-[#C9C6C6]" />
            <div className="text-lg font-editorial text-[#EEEEEF]">No jobs here.</div>
          </div>
        ) : (
          <div className="bg-[#0D1421] border border-[#C9C6C6]/10">
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b border-[#C9C6C6]/10 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
              <div className="col-span-2">Invoice</div>
              <div className="col-span-3">Service</div>
              <div className="col-span-3">Pro</div>
              <div className="col-span-1">Estimate</div>
              <div className="col-span-1">Days</div>
              <div className="col-span-2">Status</div>
            </div>
            {rows.map((j) => (
              <div
                key={j.id}
                className={`grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 border-b border-[#C9C6C6]/8 items-center ${
                  j.flagged ? "bg-[#010204]/5" : ""
                }`}
                data-testid={`admin-job-row-${j.id}`}
              >
                <div className="col-span-1 md:col-span-2">
                  <div className="font-mono-alt text-sm text-[#EEEEEF]">
                    #{j.invoice_number}
                  </div>
                  {j.flagged && (
                    <span
                      className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-[0.2em] font-semibold bg-[#010204] text-white px-1.5 py-0.5"
                      data-testid={`admin-job-flag-${j.id}`}
                    >
                      <Flame className="w-3 h-3" /> Flagged
                    </span>
                  )}
                </div>
                <div className="col-span-1 md:col-span-3">
                  <div className="text-sm text-[#EEEEEF] font-medium truncate">
                    {j.lead?.service_type || "—"}
                  </div>
                  <div className="text-xs text-[#C9C6C6] truncate">
                    {j.lead?.name} · {j.lead?.zip_code}
                  </div>
                </div>
                <div className="col-span-1 md:col-span-3">
                  <div className="text-sm text-[#EEEEEF] truncate">
                    {j.contractor?.business_name || j.contractor?.name || "—"}
                  </div>
                  <div className="text-xs text-[#C9C6C6] truncate">
                    {j.contractor?.email}
                  </div>
                </div>
                <div className="col-span-1 md:col-span-1 text-sm text-[#EEEEEF]">
                  ${Number(j.price_low || 0).toLocaleString()}–${Number(j.price_high || 0).toLocaleString()}
                </div>
                <div className="col-span-1 md:col-span-1 text-sm font-mono-alt text-[#EEEEEF]">
                  {j.days_since_start}d
                </div>
                <div className="col-span-1 md:col-span-2">
                  <span
                    className={`inline-block text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 ${statusColors[j.status] || "bg-[#C9C6C6]/30 text-white"}`}
                  >
                    {j.status.replace("_", " ")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 *  Reviews moderation view
 * ============================================================ */
function ReviewsView() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null); // review id
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listResp, sumResp] = await Promise.all([
        axios.get(`${API}/admin/reviews?filter=${filter}`, { withCredentials: true }),
        axios.get(`${API}/admin/reviews/summary`, { withCredentials: true }),
      ]);
      setRows(listResp.data);
      setSummary(sumResp.data);
    } catch {
      toast.error("Could not load reviews");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/reviews/${id}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success("Approved");
      load();
    } catch {
      toast.error("Could not approve");
    } finally {
      setBusy(false);
    }
  };

  const reject = async (id) => {
    if (!rejectReason.trim()) {
      toast.error("Enter a reason");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/reviews/${id}/reject`,
        { reason: rejectReason.trim() },
        { withCredentials: true }
      );
      toast.success("Rejected");
      setRejecting(null);
      setRejectReason("");
      load();
    } catch {
      toast.error("Could not reject");
    } finally {
      setBusy(false);
    }
  };

  const filters = [
    { id: "pending", label: `Pending${summary?.pending ? " · " + summary.pending : ""}` },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "all", label: "All" },
  ];

  return (
    <div data-testid="reviews-view">
      {/* Summary */}
      <div className="bg-[#0D1421] border-b border-[#C9C6C6]/8">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "Pending", value: summary?.pending ?? 0 },
            { label: "Approved", value: summary?.approved ?? 0 },
            { label: "Rejected", value: summary?.rejected ?? 0 },
            { label: "Total", value: summary?.total ?? 0 },
          ].map((s) => (
            <div key={s.label} data-testid={`reviews-stat-${s.label.toLowerCase()}`}>
              <div className="font-editorial text-4xl font-light text-[#EEEEEF] tracking-tighter leading-none">
                {s.value}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#0D1421] border-b border-[#C9C6C6]/8">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-3 flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-[11px] uppercase tracking-[0.22em] font-semibold px-3 py-2 border transition-colors ${
                filter === f.id
                  ? "bg-[#010204] text-white border-[#C9C6C6]"
                  : "bg-transparent text-[#EEEEEF] border-[#C9C6C6]/20 hover:border-[#C9C6C6]"
              }`}
              data-testid={`reviews-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 space-y-4">
        {loading ? (
          <div className="p-10 flex items-center gap-3 text-[#C9C6C6]">
            <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC]" /> Loading reviews…
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-[#C9C6C6]/20 p-12 text-center">
            <Star className="w-8 h-8 mx-auto mb-3 text-[#C9C6C6]" />
            <div className="text-lg font-editorial text-[#EEEEEF]">
              No reviews in &quot;{filter}&quot;.
            </div>
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className="bg-[#0D1421] border border-[#C9C6C6]/10 p-5"
              data-testid={`review-row-${r.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-4 h-4 ${n <= r.rating ? "fill-[#0E93EC] text-[#0E93EC]" : "text-[#C9C6C6]"}`}
                        />
                      ))}
                    </div>
                    <span className="text-[11px] font-mono-alt text-[#C9C6C6]">
                      #{r.invoice_number}
                    </span>
                  </div>
                  <div className="text-sm text-[#EEEEEF] font-medium">
                    {r.homeowner_name || "Homeowner"} →{" "}
                    <b className="text-[#EEEEEF]">
                      {r.contractor?.business_name || "—"}
                    </b>
                  </div>
                  <div className="text-[11px] text-[#C9C6C6] mt-0.5">
                    {r.contractor?.email} · {r.contractor?.trade}
                  </div>
                </div>
                <StatusBadge status={r.status === "pending" ? "pending" : r.status === "approved" ? "verified" : "rejected"} />
              </div>
              <p className="mt-3 text-sm text-[#EEEEEF] whitespace-pre-wrap leading-relaxed">
                {r.body}
              </p>
              {r.job_scope && (
                <details className="mt-2">
                  <summary className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C9C6C6] cursor-pointer">
                    Job scope
                  </summary>
                  <div className="mt-1 text-xs text-[#C9C6C6] whitespace-pre-wrap border-l-2 border-[#C9C6C6]/15 pl-3">
                    {r.job_scope}
                  </div>
                </details>
              )}
              {r.status === "rejected" && r.moderator_note && (
                <div className="mt-3 text-xs text-[#EEEEEF]">
                  Rejection reason: {r.moderator_note}
                </div>
              )}
              {r.status === "pending" && (
                <div className="mt-4">
                  {rejecting === r.id ? (
                    <div className="space-y-2">
                      <Label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]">
                        Rejection reason (visible to homeowner)
                      </Label>
                      <Textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={2}
                        placeholder="e.g. Contains personal info / profanity / off-topic."
                        className="bg-[#010204] border-[#C9C6C6]/15 rounded-none focus-visible:ring-2 focus-visible:ring-[#C9C6C6] focus-visible:ring-offset-0"
                        data-testid={`review-reject-reason-${r.id}`}
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={() => reject(r.id)}
                          disabled={busy || !rejectReason.trim()}
                          className="rounded-none bg-[#010204] hover:bg-[#C9C6C6] text-white h-10 px-5"
                          data-testid={`review-reject-confirm-${r.id}`}
                        >
                          Confirm rejection
                        </Button>
                        <Button
                          onClick={() => {
                            setRejecting(null);
                            setRejectReason("");
                          }}
                          variant="ghost"
                          className="rounded-none h-10 px-4 text-[#C9C6C6]"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => approve(r.id)}
                        disabled={busy}
                        className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 px-5"
                        data-testid={`review-approve-${r.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button
                        onClick={() => setRejecting(r.id)}
                        variant="outline"
                        className="rounded-none border-[#C9C6C6] text-[#EEEEEF] hover:bg-[#010204]/5 h-10 px-5"
                        data-testid={`review-reject-open-${r.id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ============================================================
 *  Admin Dashboard root
 * ============================================================ */
export default function AdminDashboard() {
  const { authed, loading: authLoading, logout } = useAdminAuth();
  const [tab, setTab] = useState("verifications");

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#010204] text-[#C9C6C6]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading…
      </div>
    );
  }
  if (!authed) return <Navigate to="/admin/login" replace />;

  const tabs = [
    { id: "verifications", label: "Verifications", icon: ShieldCheck },
    { id: "jobs", label: "Active jobs", icon: Briefcase },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "messages", label: "Messages", icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-[#010204]" data-testid="admin-dashboard">
      <AdminTopBar onLogout={logout} summary={null} />

      {/* Tabs */}
      <div className="bg-[#0D1421] border-b border-[#C9C6C6]/8">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 flex overflow-x-auto scrollbar-hide">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`shrink-0 px-4 sm:px-5 py-4 border-b-2 transition-colors text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.22em] font-semibold flex items-center gap-2 whitespace-nowrap ${
                  tab === t.id
                    ? "border-[#0E93EC] text-[#EEEEEF]"
                    : "border-transparent text-[#C9C6C6] hover:text-[#EEEEEF]"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "verifications" && <VerificationsView />}
      {tab === "jobs" && <ActiveJobsView />}
      {tab === "reviews" && <ReviewsView />}
      {tab === "messages" && <MessagesView />}
    </div>
  );
}

/* ============================================================
 *  Verifications view (original)
 * ============================================================ */
function VerificationsView() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listResp, sumResp] = await Promise.all([
        axios.get(`${API}/admin/verifications?filter=${filter}`, {
          withCredentials: true,
        }),
        axios.get(`${API}/admin/summary`, { withCredentials: true }),
      ]);
      setRows(listResp.data);
      setSummary(sumResp.data);
    } catch {
      toast.error("Could not load admin data");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const openContractor = (id) => {
    setSelectedId(id);
    setDrawerOpen(true);
  };

  return (
    <>
      {/* Summary tiles */}
      <div className="bg-[#0D1421] border-b border-[#C9C6C6]/8">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "Pending", value: summary?.pending ?? 0, testid: "summary-pending" },
            { label: "Verified", value: summary?.verified ?? 0, testid: "summary-verified" },
            { label: "Rejected", value: summary?.rejected ?? 0, testid: "summary-rejected" },
            { label: "Total pros", value: summary?.total ?? 0, testid: "summary-total" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid}>
              <div className="font-editorial text-4xl font-light text-[#EEEEEF] tracking-tighter leading-none">
                {s.value}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-[#0D1421] border-b border-[#C9C6C6]/8">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-3 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-[11px] uppercase tracking-[0.22em] font-semibold px-3 py-2 border transition-colors ${
                filter === f.id
                  ? "bg-[#010204] text-white border-[#C9C6C6]"
                  : "bg-transparent text-[#EEEEEF] border-[#C9C6C6]/20 hover:border-[#C9C6C6]"
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Verifications table */}
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        {loading ? (
          <div className="p-10 flex items-center gap-3 text-[#C9C6C6]">
            <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC]" />
            Loading pros…
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-[#C9C6C6]/20 p-12 text-center">
            <ShieldCheck className="w-8 h-8 mx-auto mb-3 text-[#C9C6C6]" />
            <div className="text-lg font-editorial text-[#EEEEEF]">
              Nothing in &quot;{FILTERS.find((f) => f.id === filter)?.label}&quot;
            </div>
            <div className="mt-2 text-sm text-[#C9C6C6]">
              Pros will appear here once they submit docs.
            </div>
          </div>
        ) : (
          <div className="bg-[#0D1421] border border-[#C9C6C6]/10">
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b border-[#C9C6C6]/10 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
              <div className="col-span-3">Pro</div>
              <div className="col-span-2">Trade</div>
              <div className="col-span-2">Service area</div>
              <div className="col-span-1">Docs</div>
              <div className="col-span-2">Submitted</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {rows.map((c) => (
              <motion.button
                layout
                key={c.id}
                onClick={() => openContractor(c.id)}
                className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 border-b border-[#C9C6C6]/8 hover:bg-[#010204] transition-colors items-center"
                data-testid={`verification-row-${c.id}`}
              >
                <div className="col-span-1 md:col-span-3">
                  <div className="font-medium text-[#EEEEEF]">
                    {c.business_name || c.name || "—"}
                  </div>
                  <div className="text-xs text-[#C9C6C6] truncate">
                    {c.email}
                  </div>
                </div>
                <div className="col-span-1 md:col-span-2 text-sm text-[#EEEEEF]">
                  {c.trade || "—"}
                </div>
                <div className="col-span-1 md:col-span-2 text-sm text-[#EEEEEF] truncate">
                  {c.service_area || "—"}
                </div>
                <div className="col-span-1 md:col-span-1">
                  <span className="font-mono-alt text-sm text-[#EEEEEF]">
                    {c.doc_count}
                  </span>
                </div>
                <div className="col-span-1 md:col-span-2 text-xs text-[#C9C6C6] font-mono-alt">
                  {timeAgo(c.submitted_for_verification_at)}
                </div>
                <div className="col-span-1 md:col-span-2 flex md:justify-end items-center gap-2">
                  <StatusBadge status={c.verification_status} />
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <ContractorDrawer
        contractorId={selectedId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onDone={load}
      />
    </>
  );
}


/* ============================================================
 *  Messages view — admin read-only across all job threads
 * ============================================================ */
function formatDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function MessagesView() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadData, setThreadData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages/recent`);
      setThreads(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load message threads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openThread = async (jobId) => {
    setSelectedJob(jobId);
    setThreadLoading(true);
    setThreadData(null);
    try {
      const { data } = await axios.get(`${API}/admin/jobs/${jobId}/messages`);
      setThreadData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load messages");
      setThreadData(null);
    } finally {
      setThreadLoading(false);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8" data-testid="admin-messages-view">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-2">
            — Moderation
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl font-light text-[#EEEEEF] tracking-tighter">
            Job message board
          </h2>
          <p className="text-sm text-[#C9C6C6] mt-2 max-w-xl">
            Read-only view of every homeowner &lt;&gt; pro conversation. Sorted by most recent activity.
          </p>
        </div>
        <Button
          onClick={load}
          variant="ghost"
          className="rounded-none border border-[#C9C6C6]/10 text-[#C9C6C6] hover:text-[#EEEEEF]"
          data-testid="admin-messages-refresh"
        >
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#C9C6C6] py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading threads…
        </div>
      ) : threads.length === 0 ? (
        <div className="border border-dashed border-[#C9C6C6]/20 p-12 text-center bg-[#0D1421]">
          <MessageSquare className="w-6 h-6 text-[#C9C6C6] mx-auto mb-3" />
          <div className="font-editorial text-2xl text-[#EEEEEF]">No message threads yet.</div>
          <div className="text-[#C9C6C6] text-sm mt-1">
            Threads appear once a homeowner or pro sends the first message on an accepted job.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Threads list */}
          <div className="space-y-3" data-testid="admin-messages-thread-list">
            {threads.map((t) => (
              <button
                key={t.job_id}
                onClick={() => openThread(t.job_id)}
                className={`w-full text-left border p-4 bg-[#010204] hover:border-[#0E93EC] transition-colors ${
                  selectedJob === t.job_id
                    ? "border-[#0E93EC] bg-[#0E93EC]/5"
                    : "border-[#C9C6C6]/10"
                }`}
                data-testid={`admin-thread-${t.job_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
                    #{t.invoice_number || t.job_id.slice(0, 8)} · {t.status?.replace("_", " ")}
                  </div>
                  <div className="text-[11px] text-[#C9C6C6]">
                    {formatDateTime(t.last_at)}
                  </div>
                </div>
                <div className="font-editorial text-lg text-[#EEEEEF] tracking-tight">
                  {t.service_type || "Home project"}
                </div>
                <div className="text-xs text-[#C9C6C6] mt-0.5">
                  {t.homeowner_name} &lt;&gt; {t.contractor_name || "Pro"}
                </div>
                <div className="mt-2 text-sm text-[#EEEEEF] line-clamp-2">
                  <span
                    className={`text-[10px] uppercase tracking-[0.22em] font-semibold mr-2 ${
                      t.last_sender_role === "homeowner" ? "text-[#0E93EC]" : "text-[#0E93EC]"
                    }`}
                  >
                    {t.last_sender_role === "homeowner" ? "Homeowner" : "Pro"}:
                  </span>
                  {t.last_body || <span className="italic text-[#C9C6C6]">(photo)</span>}
                </div>
                <div className="mt-2 text-[11px] text-[#C9C6C6]">
                  {t.message_count} message{t.message_count === 1 ? "" : "s"}
                </div>
              </button>
            ))}
          </div>

          {/* Selected thread */}
          <div className="lg:sticky lg:top-6 lg:self-start" data-testid="admin-messages-thread-panel">
            {!selectedJob ? (
              <div className="border border-dashed border-[#C9C6C6]/20 p-12 text-center bg-[#0D1421]">
                <MessageSquare className="w-6 h-6 text-[#C9C6C6] mx-auto mb-3" />
                <div className="text-[#C9C6C6] text-sm">Select a thread to review the conversation.</div>
              </div>
            ) : threadLoading ? (
              <div className="flex items-center gap-2 text-[#C9C6C6] py-8">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading conversation…
              </div>
            ) : threadData ? (
              <div>
                <div className="mb-3 border border-[#C9C6C6]/10 bg-[#010204] p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">
                    Job #{threadData.job?.invoice_number || threadData.job?.id?.slice(0, 8)} ·{" "}
                    {threadData.job?.status?.replace("_", " ")}
                  </div>
                  <div className="text-sm text-[#EEEEEF] whitespace-pre-wrap">
                    {threadData.job?.scope || "—"}
                  </div>
                </div>
                <MessageBoard
                  jobId={selectedJob}
                  viewerRole="admin"
                  readOnly
                  initialMessages={threadData.messages || []}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
